import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Crown, Upload, CheckCircle, Clock, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';

export function PremiumPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [hasRequest, setHasRequest] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.is_premium) {
      navigate('/');
      return;
    }
    checkPremiumStatus();
  }, [user, navigate]);

  const checkPremiumStatus = async () => {
    try {
      const res = await axios.get(`${API}/premium/status`, { withCredentials: true });
      setPremiumStatus(res.data);
      setHasRequest(res.data.has_request);
    } catch (error) {
      console.error('Failed to check premium status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPremium = async () => {
    try {
      await axios.post(`${API}/premium/request`, {}, { withCredentials: true });
      toast.success('Solicitação premium enviada!');
      await checkPremiumStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar premium');
    }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/premium/upload-receipt`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Comprovante enviado com sucesso!');
      await checkPremiumStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-[#0066FF] text-xl">Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/af04cee4-1d95-42e7-9f81-e2167e421d48/images/0e5986c40088a6b1dbd0be4debeea8ac3c39f98ef2f8987297d502dd46356bbf.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <Navbar />

      <main className="relative z-10 mt-16 pt-12 pb-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-[#0066FF] to-[#00FF99] rounded-2xl mb-6">
              <Crown className="w-10 h-10 text-black" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white mb-4">
              Upgrade para Premium
            </h1>
            <p className="text-lg text-zinc-300 max-w-2xl mx-auto">
              Desbloqueie coleções exclusivas de STL, insights do Etsy e análises de tendências virais
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Recursos Premium</h2>
              <ul className="space-y-4">
                {[
                  'Acesso a todas as coleções premium de STL',
                  'Impressões virais exclusivas curadas',
                  'Sugestões de SEO e preços para Etsy',
                  'Ganchos de marketing para TikTok',
                  'Alertas de tendências virais',
                  'Mockups gerados por IA',
                  'Suporte prioritário'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-[#00FF99] mt-0.5 shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              data-testid="payment-section"
              className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Informações de Pagamento</h2>
              
              {!hasRequest ? (
                <>
                  <div className="bg-[#0066FF]/10 border border-[#0066FF]/30 rounded-lg p-6 mb-6">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-[#0066FF] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-white font-semibold mb-2">Em Construção</p>
                        <p className="text-zinc-300 text-sm">
                          Integração de pagamento em breve. Por enquanto, o acesso premium é aprovado manualmente pela nossa equipe.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6 opacity-50">
                    <div className="bg-[#1A1A1A] border border-[#27272A] rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-2">Pagamento PIX</h3>
                      <p className="text-zinc-400 text-sm">Em breve...</p>
                    </div>
                    <div className="bg-[#1A1A1A] border border-[#27272A] rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-2">Pagamento Zelle</h3>
                      <p className="text-zinc-400 text-sm">Em breve...</p>
                    </div>
                  </div>

                  <button
                    data-testid="request-premium-button"
                    onClick={handleRequestPremium}
                    className="w-full bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black hover:opacity-90 transition-all duration-300 font-bold rounded-lg px-6 py-3.5"
                  >
                    Solicitar Acesso Premium
                  </button>
                </>
              ) : (
                <div className="space-y-6">
                  {premiumStatus.status === 'pending' && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <Clock className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-white font-semibold mb-1">Solicitação Pendente</p>
                          <p className="text-zinc-300 text-sm">
                            Sua solicitação premium está sendo analisada pela nossa equipe. Você será notificado quando aprovada.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {premiumStatus.status === 'approved' && (
                    <div className="bg-[#00FF99]/10 border border-[#00FF99]/30 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-[#00FF99] mt-0.5 shrink-0" />
                        <div>
                          <p className="text-white font-semibold mb-1">Aprovado!</p>
                          <p className="text-zinc-300 text-sm">
                            Seu acesso premium foi ativado. Atualize a página para ver seu novo status.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {premiumStatus.status === 'rejected' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <Info className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-white font-semibold mb-1">Solicitação Rejeitada</p>
                          <p className="text-zinc-300 text-sm">
                            Entre em contato com o suporte para mais informações.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {premiumStatus.status === 'pending' && !premiumStatus.receipt_uploaded && (
                    <div>
                      <label
                        data-testid="upload-receipt-section"
                        className="block w-full bg-[#1A1A1A] border-2 border-dashed border-[#27272A] hover:border-[#0066FF] rounded-lg p-8 cursor-pointer transition-all"
                      >
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handleReceiptUpload}
                          disabled={uploading}
                          className="hidden"
                          data-testid="receipt-file-input"
                        />
                        <div className="text-center">
                          <Upload className="w-12 h-12 text-[#0066FF] mx-auto mb-3" />
                          <p className="text-white font-semibold mb-1">
                            {uploading ? 'Uploading...' : 'Upload Payment Receipt'}
                          </p>
                          <p className="text-zinc-400 text-sm">Click to select file</p>
                        </div>
                      </label>
                    </div>
                  )}

                  {premiumStatus.receipt_uploaded && (
                    <div className="bg-[#00FF99]/10 border border-[#00FF99]/30 rounded-lg p-6">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-[#00FF99] mt-0.5 shrink-0" />
                        <div>
                          <p className="text-white font-semibold">Comprovante Enviado</p>
                          <p className="text-zinc-300 text-sm">Estamos analisando sua submissão.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}